import React, { useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Umbrella, CalendarDays, Loader2 } from 'lucide-react';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); HolidaysEventsModal.scss deleted
// with it, and with it a whole `[data-theme="dark"]` block that existed only to
// restate four colours in a lighter blue. The accent was #3b82f6 / #60a5fa — a
// blue, on a violet app, so the active tab was the one thing on this modal that
// never followed the institute's brand. It is --primary now and the dark fork is
// unnecessary, because the token already changes underneath.
//
// The dots keep their meaning rather than their hex: a holiday is --danger, an
// event is --primary. Both invert for free.
const TAB =
  'tw--mb-px tw-flex tw-cursor-pointer tw-items-center tw-gap-[0.3rem] tw-whitespace-nowrap ' +
  'tw-border-0 tw-border-b-2 tw-border-solid tw-bg-transparent tw-px-3.5 tw-py-[0.6rem] ' +
  'tw-text-[0.8125rem] tw-font-semibold tw-transition-[color,border-color] tw-duration-[120ms]';
const TAB_IDLE = 'tw-border-b-transparent tw-text-content-muted hover:tw-text-content';
const TAB_ACTIVE = 'tw-border-b-primary tw-text-primary';

const COUNT =
  'tw-ms-[0.1rem] tw-inline-flex tw-h-[18px] tw-min-w-[18px] tw-items-center tw-justify-center ' +
  'tw-rounded-full tw-px-1 tw-text-[0.6875rem] tw-font-bold';
const COUNT_IDLE = 'tw-bg-surface-alt tw-text-content-muted';
const COUNT_ACTIVE = 'tw-bg-primary/[0.12] tw-text-primary';

const STATE =
  'tw-flex tw-min-h-[240px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 ' +
  'tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-content-muted';

const ITEM = 'tw-flex tw-items-center tw-gap-2.5 tw-px-5 tw-py-2.5 tw-transition-colors tw-duration-100';
const ITEM_CLICKABLE = 'tw-cursor-pointer hover:tw-bg-surface-alt';

const fmt = (d) =>
  new Date(d).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
  });

const fmtRange = (evt) => {
  const s = fmt(evt.startDate);
  const e = fmt(evt.endDate);
  return s === e ? s : `${s} – ${e}`;
};

const HolidaysEventsModal = ({ show, onHide, events, loading, onEventClick, isAdmin }) => {
  const [activeTab, setActiveTab] = useState('holidays');

  const holidays = useMemo(
    () => (events || []).filter((e) => e.type === 'HOLIDAY').sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
    [events],
  );
  const otherEvents = useMemo(
    () => (events || []).filter((e) => e.type !== 'HOLIDAY').sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
    [events],
  );

  const items = activeTab === 'holidays' ? holidays : otherEvents;
  // Holiday = --danger, event = --primary. The old sheet wrote #ef4444 / #3b82f6
  // and then restated both in dark; a token needs neither.
  const dotTone = activeTab === 'holidays' ? 'tw-bg-danger' : 'tw-bg-primary';

  const tab = (key, Icon, label, count) => (
    <button
      type="button"
      className={`${TAB} ${activeTab === key ? TAB_ACTIVE : TAB_IDLE}`}
      onClick={() => setActiveTab(key)}
    >
      <Icon size={13} /> {label}
      <span className={`${COUNT} ${activeTab === key ? COUNT_ACTIVE : COUNT_IDLE}`}>{count}</span>
    </button>
  );

  return (
    // Reaching into react-bootstrap's own markup is what an arbitrary variant is
    // for (TOKEN_CONTRACT §6.3b) — .modal-content and .modal-header are Modal's
    // classes, not ours, so they are styled at the element that owns the reach.
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="[&_.modal-content]:tw-overflow-hidden [&_.modal-content]:tw-rounded-[14px] [&_.modal-content]:tw-border [&_.modal-content]:tw-border-line [&_.modal-content]:tw-bg-surface-raised [&_.modal-header]:tw-border-b [&_.modal-header]:tw-border-line [&_.modal-header]:tw-bg-surface-raised [&_.modal-header]:tw-px-5 [&_.modal-header]:tw-py-3.5"
    >
      <Modal.Header closeButton>
        <Modal.Title className="tw-flex tw-items-center tw-gap-[0.4rem] tw-text-[0.9375rem] tw-font-semibold tw-text-content">
          <CalendarDays size={16} /> Holidays &amp; Events
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* Tabs */}
        <div className="tw-flex tw-border-b tw-border-line tw-bg-surface-raised tw-px-3">
          {tab('holidays', Umbrella, 'Holidays', holidays.length)}
          {tab('events', CalendarDays, 'Events', otherEvents.length)}
        </div>

        {/* Content */}
        {loading ? (
          <div className={STATE}>
            <Loader2 size={22} className="tw-animate-spin tw-text-primary" />
            <span>Loading…</span>
          </div>
        ) : items.length === 0 ? (
          <div className={STATE}>
            No {activeTab === 'holidays' ? 'holidays' : 'events'} found for this period.
          </div>
        ) : (
          <div className="tw-max-h-[380px] tw-min-h-[240px] tw-overflow-y-auto tw-py-1.5">
            {items.map((evt) => (
              <div
                key={evt._id}
                className={`${ITEM} ${isAdmin && onEventClick ? ITEM_CLICKABLE : ''}`}
                onClick={isAdmin && onEventClick ? () => { onHide(); onEventClick(evt); } : undefined}
                role={isAdmin && onEventClick ? 'button' : undefined}
                tabIndex={isAdmin && onEventClick ? 0 : undefined}
                onKeyDown={isAdmin && onEventClick ? (e) => e.key === 'Enter' && onEventClick(evt) : undefined}
              >
                <span className={`tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full ${dotTone}`} />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-content">{evt.title}</span>
                <span className="tw-shrink-0 tw-whitespace-nowrap tw-text-xs tw-text-content-muted">{fmtRange(evt)}</span>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default HolidaysEventsModal;
